/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { updateUploadStatus } from 'Api/apiFunctions';
import { axios, baseUrl, getToken } from 'Api';
import {
  createUploadSession,
  completeUploadSession,
  uploadPartWithPresignedUrl,
  registerDocument,
  updateDraftLink,
} from 'Api/uploaderApiFunctions';
import { getBucketNamePath } from 'Api/awsConfig';
import styles from './MobileUpload.module.css';

const UPLOAD_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  PAUSED: 'paused',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
};

// Convert base64 data URL to binary blob for API (same as main app)
const base64ToBinary = (base64String) => {
  const [, base64Data] = base64String.split(',');
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  const binaryData = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) {
    binaryData[i] = binaryString.charCodeAt(i);
  }
  return new Blob([binaryData], { type: 'image/jpeg' });
};

// Bytes to MB string (same as main app's changeSize)
const changeSize = (size) => {
  const newSize = (size || 0) / 1024 / 1024;
  return newSize.toFixed(2);
};

// Get video dimensions from a file
const getVideoDimensions = (file) => new Promise((resolve) => {
  const video = document.createElement('video');
  const url = URL.createObjectURL(file);
  video.preload = 'metadata';
  video.muted = true;
  const cleanup = () => { try { URL.revokeObjectURL(url); video.remove(); } catch (e) { /* */ } };
  const timeout = setTimeout(() => { cleanup(); resolve({ width: 0, height: 0 }); }, 5000);
  video.onloadedmetadata = () => {
    clearTimeout(timeout);
    const w = video.videoWidth || 0;
    const h = video.videoHeight || 0;
    cleanup();
    resolve({ width: w, height: h });
  };
  video.onerror = () => { clearTimeout(timeout); cleanup(); resolve({ width: 0, height: 0 }); };
  video.src = url;
  video.load();
});

const MobileUpload = () => {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session');
  const tokenFromUrl = params.get('token');
  const accessFromUrl = params.get('access');

  const fileInputRef = useRef(null);
  const fileUploadBatchCounter = useRef(0);

  const [files, setFiles] = useState([]);
  const [contentFilter, setContentFilter] = useState('all');
  const [showCompletePopup, setShowCompletePopup] = useState(false);
  const [albumModal, setAlbumModal] = useState(null); // { photos: [...] }

  // Store per-file metadata for registration (thumbnail, dimensions)
  const fileMetadataRef = useRef({});
  // Store active XHR refs per file for cancel/abort
  const activeXhrRefs = useRef({});
  // Photo album ID — created once, reused for all photos (same as QuickUserUploadPage)
  const photoAlbumIdRef = useRef(null);
  // Track cancelled file IDs (ref so async upload loop can read it without stale closure)
  const cancelledIdsRef = useRef(new Set());
  // Track paused file IDs + resolve functions to resume
  const pausedIdsRef = useRef(new Map()); // fileId -> resumeResolve

  useEffect(() => {
    if (accessFromUrl) {
      // console.log('[MobileUpload] Setting uploader token from URL');
      localStorage.setItem('uploader_token', accessFromUrl);
    }
    if (tokenFromUrl) {
      // console.log('[MobileUpload] Setting auth token from URL');
      localStorage.setItem('token', tokenFromUrl);
    }
    const handleBeforeUnload = (e) => {
      if (files.some((f) => f.status === UPLOAD_STATUS.UPLOADING)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [accessFromUrl, tokenFromUrl, files]);

  // Group photos into a single album entry for display (same as gift flow)
  const displayFiles = useMemo(() => {
    const videos = files.filter((f) => f.type === 'video');
    const photos = files.filter((f) => f.type === 'image');
    if (photos.length === 0) return videos;

    const completedPhotos = photos.filter((f) => f.status === UPLOAD_STATUS.COMPLETED).length;
    const failedPhotos = photos.filter((f) => f.status === UPLOAD_STATUS.FAILED).length;
    const allDone = completedPhotos + failedPhotos === photos.length;
    const albumProgress = photos.length > 0 ? Math.round((completedPhotos / photos.length) * 100) : 0;

    const albumEntry = {
      id: 'photo-album',
      name: `All photos (${photos.length})`,
      type: 'album',
      status: allDone ? UPLOAD_STATUS.COMPLETED : UPLOAD_STATUS.UPLOADING,
      progress: albumProgress,
      thumbnailUrl: photos[0]?.thumbnailUrl || null,
      totalPhotos: photos.length,
      uploadedPhotos: completedPhotos,
      isPhotoAlbum: true,
      // Keep individual photo IDs for cancel
      photoIds: photos.map((p) => p.id),
    };

    return [...videos, albumEntry];
  }, [files]);

  const filteredFiles = useMemo(() => {
    if (contentFilter === 'all') return displayFiles;
    if (contentFilter === 'videos') return displayFiles.filter((f) => f.type === 'video');
    if (contentFilter === 'photos') return files.filter((f) => f.type === 'image');
    return displayFiles;
  }, [displayFiles, files, contentFilter]);

  const videoCount = files.filter((f) => f.type === 'video').length;
  const photoCount = files.filter((f) => f.type === 'image').length;

  if (!sessionId) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="#EF4444">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <p className={styles.errorStateText}>Invalid link. Please scan the QR code again.</p>
        </div>
      </div>
    );
  }

  const sendStatus = async (statusData) => {
    try {
      // console.log('[MobileUpload] Sending status to desktop:', statusData);
      await updateUploadStatus({ session_id: sessionId, status_data: JSON.stringify(statusData) });
      // console.log('[MobileUpload] Status sent successfully:', statusData.action);
    } catch (err) {
      console.error('[MobileUpload] Failed to send status:', err);
    }
  };

  // iOS-compatible video thumbnail capture (same as gift flow).
  // Off-DOM video + canvas.drawImage produces black frames on iOS because
  // GPU-decoded frames aren't accessible. Multiple fallback methods are used.
  const captureVideoFrame = (file) => new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(null), 6000);
    try {
      const video = document.createElement('video');
      const videoUrl = URL.createObjectURL(file);
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      const cleanup = () => {
        try { URL.revokeObjectURL(videoUrl); } catch (e) { /* ignore */ }
        try { video.remove(); } catch (e) { /* ignore */ }
      };

      video.onloadedmetadata = () => {
        if (video.videoWidth === 0 || video.videoHeight === 0) {
          clearTimeout(timeout); cleanup(); resolve(null);
          return;
        }
        video.currentTime = 0.5;
      };

      video.onseeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 320;
          canvas.height = video.videoHeight || 180;
          const ctx = canvas.getContext('2d');
          if (!ctx) { clearTimeout(timeout); cleanup(); resolve(null); return; }
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
          canvas.remove();
          clearTimeout(timeout);
          cleanup();
          // Check if frame is not black (data URL length > 1000 means actual content)
          resolve(dataUrl && dataUrl.length > 1000 ? dataUrl : null);
        } catch (e) {
          clearTimeout(timeout); cleanup(); resolve(null);
        }
      };

      video.onerror = () => { clearTimeout(timeout); cleanup(); resolve(null); };
      video.src = videoUrl;
      video.load();
    } catch (e) {
      clearTimeout(timeout); resolve(null);
    }
  });

  // Fallback: capture from an already-rendered <video> element on screen (iOS workaround).
  // On iOS, rendered on-screen video elements can sometimes provide frames
  // when off-DOM elements cannot.
  const captureFromRenderedVideo = (blobUrl) => {
    try {
      const videoEl = document.querySelector(`video[src="${CSS.escape(blobUrl)}"]`);
      if (!videoEl || videoEl.videoWidth === 0 || videoEl.videoHeight === 0) return null;

      const maxWidth = 320;
      const scale = Math.min(1, maxWidth / videoEl.videoWidth);
      const w = Math.round(videoEl.videoWidth * scale);
      const h = Math.round(videoEl.videoHeight * scale);

      // Method 1: WebGL — different GPU pathway, may access video frames on iOS
      try {
        const glCanvas = document.createElement('canvas');
        glCanvas.width = w;
        glCanvas.height = h;
        const gl = glCanvas.getContext('webgl', { preserveDrawingBuffer: true });
        if (gl) {
          const texture = gl.createTexture();
          gl.bindTexture(gl.TEXTURE_2D, texture);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, videoEl);
          const vs = gl.createShader(gl.VERTEX_SHADER);
          gl.shaderSource(vs, 'attribute vec2 a_pos;varying vec2 v_tc;void main(){gl_Position=vec4(a_pos,0,1);v_tc=(a_pos+1.0)/2.0;v_tc.y=1.0-v_tc.y;}');
          gl.compileShader(vs);
          const fs = gl.createShader(gl.FRAGMENT_SHADER);
          gl.shaderSource(fs, 'precision mediump float;varying vec2 v_tc;uniform sampler2D u_tex;void main(){gl_FragColor=texture2D(u_tex,v_tc);}');
          gl.compileShader(fs);
          const prog = gl.createProgram();
          gl.attachShader(prog, vs);
          gl.attachShader(prog, fs);
          gl.linkProgram(prog);
          gl.useProgram(prog);
          const buf = gl.createBuffer();
          gl.bindBuffer(gl.ARRAY_BUFFER, buf);
          gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
          const loc = gl.getAttribLocation(prog, 'a_pos');
          gl.enableVertexAttribArray(loc);
          gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
          gl.viewport(0, 0, w, h);
          gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
          const dataUrl = glCanvas.toDataURL('image/jpeg', 0.5);
          gl.deleteTexture(texture);
          gl.deleteBuffer(buf);
          gl.deleteProgram(prog);
          gl.deleteShader(vs);
          gl.deleteShader(fs);
          glCanvas.remove();
          if (dataUrl && dataUrl.length > 1000) return dataUrl;
        }
      } catch (e) { /* WebGL failed */ }

      // Method 2: 2D canvas drawImage
      try {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(videoEl, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.5);
          canvas.remove();
          if (dataUrl && dataUrl.length > 1000) return dataUrl;
        }
      } catch (e) { /* 2D canvas failed */ }

      // Method 3: createPattern — different internal rendering path
      try {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          const pattern = ctx.createPattern(videoEl, 'no-repeat');
          if (pattern) {
            ctx.scale(w / videoEl.videoWidth, h / videoEl.videoHeight);
            ctx.fillStyle = pattern;
            ctx.fillRect(0, 0, videoEl.videoWidth, videoEl.videoHeight);
            const dataUrl = canvas.toDataURL('image/jpeg', 0.5);
            canvas.remove();
            if (dataUrl && dataUrl.length > 1000) return dataUrl;
          }
        }
      } catch (e) { /* pattern failed */ }

      return null;
    } catch (e) {
      return null;
    }
  };

  // iOS play-and-capture fallback: append a tiny hidden video, play it to force
  // frame decode, then capture from the rendered element (same pattern as gift flow).
  const captureVideoWithPlayFallback = (file) => new Promise((resolve) => {
    const video = document.createElement('video');
    const blobUrl = URL.createObjectURL(file);
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      try { URL.revokeObjectURL(blobUrl); } catch (e) { /* ignore */ }
      try { video.pause(); video.remove(); } catch (e) { /* ignore */ }
    };

    timeoutId = setTimeout(() => { cleanup(); resolve(null); }, 8000);
    video.src = blobUrl;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;opacity:0.01;pointer-events:none;';
    document.body.appendChild(video);

    video.onloadeddata = () => {
      video.currentTime = 0.5;
    };

    video.onseeked = () => {
      // Wait a tick for the frame to render
      requestAnimationFrame(() => {
        setTimeout(() => {
          const result = captureFromRenderedVideo(blobUrl);
          cleanup();
          resolve(result);
        }, 100);
      });
    };

    video.onerror = () => { cleanup(); resolve(null); };
    video.load();
  });

  const generateThumbnail = async (file) => {
    if (!file.type.startsWith('video/')) {
      return file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    }
    // Try off-DOM capture first (works on most browsers)
    const frame = await captureVideoFrame(file);
    if (frame) return frame;
    // iOS fallback: append hidden video, play to force decode, capture from rendered element
    const playCapture = await captureVideoWithPlayFallback(file);
    if (playCapture) return playCapture;
    return null;
  };

  // Track all completed file names for cumulative fileComplete messages (same as gift flow)
  const completedFileNamesRef = useRef([]);
  // Track s3Keys per fileId so every fileComplete sends all keys
  const completedS3KeysRef = useRef({});

  const uploadFile = async (file, fileId) => {
    try {
      const userId = localStorage.getItem('id') || 'unknown';
      const s3KeyPath = getBucketNamePath(userId);
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const number = Math.floor(Math.random() * 10000) + 1;
      const uniqueFileName = `${Date.now()}-${userId}-video-${number}.${fileExtension}`;
      const s3Key = `${s3KeyPath}/${uniqueFileName}`;

      // console.log('[MobileUpload] Starting upload:', { fileId, fileName: file.name, size: file.size });
      // No fileStart push — gift flow doesn't send per-file start messages
      setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, status: UPLOAD_STATUS.UPLOADING, progress: 0 } : f)));

      const session = await createUploadSession({
        file_name: file.name,
        file_size: file.size,
        content_type: file.type || 'application/octet-stream',
        s3_key: s3Key,
      });

      const { presigned_urls, part_size, total_parts } = session;
      const parts = [];
      const bytesRef = { uploaded: 0 };
      const xhrRef = { current: null };
      activeXhrRefs.current[fileId] = xhrRef;

      for (let i = 0; i < total_parts; i += 1) {
        // Check if cancelled before starting next part (use ref, not stale state)
        if (cancelledIdsRef.current.has(fileId)) {
          // console.log('[MobileUpload] Upload cancelled, stopping:', file.name);
          delete activeXhrRefs.current[fileId];
          return;
        }

        const start = i * part_size;
        const end = Math.min(start + part_size, file.size);
        const blob = file.slice(start, end);
        const partInfo = presigned_urls[i];

        let result;
        let partDone = false;
        while (!partDone) { // eslint-disable-line no-constant-condition
          try {
            // eslint-disable-next-line no-await-in-loop, no-loop-func
            result = await uploadPartWithPresignedUrl(
              partInfo.presigned_url, blob, partInfo.part_number,
              (loaded) => {
                const currentProgress = Math.round(((bytesRef.uploaded + loaded) / file.size) * 100);
                setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, progress: currentProgress } : f)));
              },
              xhrRef,
            );
            partDone = true;
          } catch (partErr) {
            // If paused, wait for resume then retry this part
            if (pausedIdsRef.current.has(fileId)) {
              // eslint-disable-next-line no-await-in-loop
              await new Promise((resolve) => { pausedIdsRef.current.set(fileId, resolve); });
              // After resume, check if cancelled during pause
              if (cancelledIdsRef.current.has(fileId)) {
                delete activeXhrRefs.current[fileId];
                return;
              }
              // Retry this part
              continue; // eslint-disable-line no-continue
            }
            // Not paused — real error, re-throw
            throw partErr;
          }
        }
        parts.push(result);
        bytesRef.uploaded += (end - start);
      }

      delete activeXhrRefs.current[fileId];

      // Check if cancelled during upload — don't register
      if (cancelledIdsRef.current.has(fileId)) {
        // console.log('[MobileUpload] Upload cancelled after parts, skipping registration:', file.name);
        return;
      }

      await completeUploadSession(session.id, parts);
      // console.log('[MobileUpload] S3 upload completed:', { fileId, fileName: file.name, s3Key });

      // ============================================================
      // Register video: registerDocument → /V6/addVideo → updateDraftLink
      // (Photos are handled separately in uploadPhotoAlbum, not here)
      // ============================================================
      try {
        // console.log('[MobileUpload] Registering video:', file.name);
        const documentResponse = await registerDocument({
          name: uniqueFileName,
          url: s3Key,
          thumbnail: s3Key,
          thumbnails: [],
          uploaded_at: new Date().toISOString(),
        });
        const documentId = documentResponse.id;

        const formData = new FormData();
        formData.append('draft_id', fileId);
        formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
        formData.append('category_id', '');
        formData.append('category_name', 'All Videos');
        formData.append('file_name', file.name);
        formData.append('file_key', s3Key);
        formData.append('document_id', documentId || '');
        formData.append('bucket_folder_path', s3Key);

        const meta = fileMetadataRef.current[fileId];
        if (meta?.thumbnail) {
          try {
            formData.append('thumbnail', base64ToBinary(meta.thumbnail));
            formData.append('thumbnails[]', base64ToBinary(meta.thumbnail));
          } catch (thumbErr) {
            console.error('[MobileUpload] Thumbnail conversion failed:', thumbErr);
          }
        }

        formData.append('description', '');
        formData.append('video_size', changeSize(file.size));
        formData.append('group_ids', JSON.stringify([]));
        formData.append('visibility', '2');
        formData.append('subcategory_id', '');
        formData.append('playlist_ids', '');
        formData.append('videoHeight', meta?.videoHeight || '');
        formData.append('videoWidth', meta?.videoWidth || '');
        formData.append('videoOreintation', meta?.videoHeight > meta?.videoWidth ? '2' : '1');
        formData.append('s3_status', 'completed');

        const addVideoResponse = await axios.post(`${baseUrl}/V6/addVideo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        // console.log('[MobileUpload] Video registered:', file.name, addVideoResponse?.data);

        if (documentId && addVideoResponse?.data?.draft_id) {
          try {
            await updateDraftLink({ document_id: documentId, draft_id: addVideoResponse.data.draft_id });
          } catch (linkErr) {
            console.error('[MobileUpload] Failed to link draft:', linkErr);
          }
        }
      } catch (regErr) {
        console.error('[MobileUpload] Registration failed for:', file.name, regErr);
      }

      setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, status: UPLOAD_STATUS.COMPLETED, progress: 100, s3Key } : f)));

      // Cumulative fileComplete — send ALL completed files so far (same as gift flow)
      completedFileNamesRef.current.push(`${file.name}|${fileId}`);
      completedS3KeysRef.current[fileId] = s3Key;
      await sendStatus({
        action: 'fileComplete',
        files: [...completedFileNamesRef.current],
        s3Keys: { ...completedS3KeysRef.current },
      });
    } catch (err) {
      // Ignore abort errors from cancel
      if (err.message === 'Upload aborted' || err.name === 'AbortError') {
        // console.log('[MobileUpload] Upload aborted (cancelled):', file.name);
        return;
      }
      console.error('[MobileUpload] Upload failed:', file.name, err);
      setFiles((prev) => prev.map((f) => (f.id === fileId ? { ...f, status: UPLOAD_STATUS.FAILED, error: err.message } : f)));
    }
  };

  // Upload photos as album — same as QuickUserUploadPage.startPhotoAlbumUpload
  const uploadPhotoAlbum = async (imageFileEntries) => {
    if (imageFileEntries.length === 0) return;

    // Find existing "All Photos" album or create one, reuse for all photos
    if (!photoAlbumIdRef.current) {
      try {
        // First, check if "All Photos" album already exists
        const albumListResponse = await axios.post(`${baseUrl}/getAlbumList`, {});
        const existingAlbums = albumListResponse.data?.data || [];
        const allPhotosAlbum = existingAlbums.find((a) => a.title === 'All Photos');
        if (allPhotosAlbum) {
          photoAlbumIdRef.current = allPhotosAlbum.album_id || allPhotosAlbum.id;
          // console.log('[MobileUpload] Found existing "All Photos" album:', photoAlbumIdRef.current);
        }
      } catch (err) {
        console.warn('[MobileUpload] Failed to fetch album list, will create new album:', err);
      }

      // If no existing album found, create a new one
      if (!photoAlbumIdRef.current) {
        try {
          // console.log('[MobileUpload] Creating "All Photos" album...');
          const albumResponse = await axios.post(`${baseUrl}/addEditAlbum`, {
            title: 'All Photos',
            description: '',
            visibility: '2',
            group_id: '',
            token: getToken(),
          });
          photoAlbumIdRef.current = albumResponse.data?.album_id;
          // console.log('[MobileUpload] Created new "All Photos" album:', photoAlbumIdRef.current);
        } catch (err) {
          console.error('[MobileUpload] Failed to create album:', err);
          imageFileEntries.forEach((entry) => {
            setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: UPLOAD_STATUS.FAILED } : f)));
          });
          return;
        }
      }
    }

    // Get icon from first photo
    let iconBase64 = null;
    try {
      iconBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(imageFileEntries[0].file);
      });
    } catch (e) { /* ignore */ }

    // Upload each photo sequentially via addPhotoContent (same as QuickUserUploadPage)
    for (let i = 0; i < imageFileEntries.length; i += 1) {
      const entry = imageFileEntries[i];

      // Check if cancelled
      if (cancelledIdsRef.current.has(entry.id)) continue; // eslint-disable-line no-continue

      setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: UPLOAD_STATUS.UPLOADING, progress: 0 } : f)));

      try {
        const photoFormData = new FormData();
        photoFormData.append('album_id', photoAlbumIdRef.current);
        photoFormData.append('visibility', '2');
        photoFormData.append('group_id', '');
        if (iconBase64) photoFormData.append('icon', iconBase64);
        photoFormData.append('token', getToken());
        photoFormData.append('photo_file[]', entry.file, entry.name);

        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${baseUrl}/addPhotoContent`, photoFormData);
        // console.log('[MobileUpload] Photo registered:', entry.name);

        setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: UPLOAD_STATUS.COMPLETED, progress: 100 } : f)));

        // SSE notification
        completedFileNamesRef.current.push(`${entry.name}|${entry.id}`);
        // eslint-disable-next-line no-await-in-loop
        await sendStatus({
          action: 'fileComplete',
          files: [...completedFileNamesRef.current],
          s3Keys: { ...completedS3KeysRef.current },
        });
      } catch (err) {
        console.error('[MobileUpload] Photo upload failed:', entry.name, err);
        setFiles((prev) => prev.map((f) => (f.id === entry.id ? { ...f, status: UPLOAD_STATUS.FAILED } : f)));
      }
    }
  };

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;
    e.target.value = '';

    // console.log('[MobileUpload] Files selected:', selectedFiles.length, selectedFiles.map((f) => f.name));
    fileUploadBatchCounter.current += 1;
    const batchId = fileUploadBatchCounter.current;

    const newFiles = selectedFiles.map((file) => ({
      id: uuid(),
      file,
      name: file.name,
      size: file.size,
      type: file.type.startsWith('video/') ? 'video' : 'image',
      status: UPLOAD_STATUS.PENDING,
      progress: 0,
      thumbnailUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      previewUrl: file.type.startsWith('video/') ? URL.createObjectURL(file) : null,
    }));

    setFiles((prev) => [...prev, ...newFiles]);

    // Separate videos and photos
    const videoFiles = newFiles.filter((f) => f.type === 'video');
    const photoFiles = newFiles.filter((f) => f.type === 'image');

    // Generate video thumbnails + collect metadata in background
    videoFiles.forEach(async (f) => {
      const [thumbUrl, dims] = await Promise.all([
        generateThumbnail(f.file),
        getVideoDimensions(f.file),
      ]);
      if (thumbUrl) {
        setFiles((prev) => prev.map((item) => (item.id === f.id ? { ...item, thumbnailUrl: thumbUrl } : item)));
      }
      fileMetadataRef.current[f.id] = {
        thumbnail: thumbUrl,
        videoHeight: dims.height,
        videoWidth: dims.width,
      };
    });

    // Generate micro thumbnails for photos to send to desktop (same as gift flow)
    const thumbs = {};
    await Promise.all(photoFiles.map((f) => new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(), 2000);
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          try {
            const maxWidth = 240;
            const scale = Math.min(1, maxWidth / img.width);
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(img.width * scale));
            canvas.height = Math.max(1, Math.round(img.height * scale));
            const ctx = canvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              thumbs[f.name] = canvas.toDataURL('image/jpeg', 0.7);
            }
            canvas.remove();
          } catch (e) { /* ignore */ }
          clearTimeout(timeout);
          resolve();
        };
        img.onerror = () => { clearTimeout(timeout); resolve(); };
        img.src = reader.result;
      };
      reader.onerror = () => { clearTimeout(timeout); resolve(); };
      reader.readAsDataURL(f.file);
    })));

    // Send SSE notification for all files (include photo thumbs in meta)
    const fileNames = newFiles.map((f) => `${f.name}|${f.id}`);
    const statusPayload = { action: `fileUpload${batchId}`, files: fileNames, fileCount: newFiles.length };
    if (Object.keys(thumbs).length > 0) {
      statusPayload.thumbs = thumbs;
    }
    await sendStatus(statusPayload);

    // Upload videos via S3 (parallel) and photos via addPhotoContent (sequential)
    const uploadPromises = [];
    if (videoFiles.length > 0) {
      uploadPromises.push(
        Promise.all(videoFiles.map((f) => uploadFile(f.file, f.id)))
      );
    }
    if (photoFiles.length > 0) {
      uploadPromises.push(uploadPhotoAlbum(photoFiles));
    }
    await Promise.all(uploadPromises);

    await sendStatus({
      action: 'allComplete',
      files: [...completedFileNamesRef.current],
      s3Keys: { ...completedS3KeysRef.current },
      fileCount: newFiles.filter((f) => f.status !== 'failed').length,
    });
  };

  const hasFiles = files.length > 0;
  const isUploading = (status) => status === UPLOAD_STATUS.UPLOADING || status === UPLOAD_STATUS.PENDING;

  const handlePauseUpload = (fileId) => {
    // Mark as paused — upload loop will block at next part boundary
    pausedIdsRef.current.set(fileId, null);
    // Abort current XHR so progress stops immediately (part will be re-uploaded on resume)
    const xhrRef = activeXhrRefs.current[fileId];
    if (xhrRef?.current) {
      try { xhrRef.current.abort(); } catch (e) { /* ignore */ }
    }
    setFiles((prev) => prev.map((f) => (f.id === fileId
      ? { ...f, status: UPLOAD_STATUS.PAUSED }
      : f)));
  };

  const handleResumeUpload = (fileId) => {
    const resolve = pausedIdsRef.current.get(fileId);
    pausedIdsRef.current.delete(fileId);
    setFiles((prev) => prev.map((f) => (f.id === fileId
      ? { ...f, status: UPLOAD_STATUS.UPLOADING }
      : f)));
    if (resolve) resolve(); // unblock the upload loop
  };

  const handleCancelUpload = (fileId) => {
    // Mark in ref so async upload loop sees it immediately
    cancelledIdsRef.current.add(fileId);
    // If paused, unblock the loop so it can exit
    const pauseResolve = pausedIdsRef.current.get(fileId);
    if (pauseResolve) pauseResolve();
    pausedIdsRef.current.delete(fileId);
    // Abort active XHR if uploading
    const xhrRef = activeXhrRefs.current[fileId];
    if (xhrRef?.current) {
      try {
        xhrRef.current.abort();
        // console.log('[MobileUpload] Aborted XHR for:', fileId);
      } catch (e) { /* ignore */ }
      delete activeXhrRefs.current[fileId];
    }
    // Mark as cancelled, then remove after delay (same as gift flow)
    setFiles((prev) => prev.map((f) => (f.id === fileId
      ? { ...f, status: UPLOAD_STATUS.CANCELLED }
      : f)));
    setTimeout(() => {
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      cancelledIdsRef.current.delete(fileId);
    }, 1000);
  };

  return (
    <div className={styles.container}>
      {/* File picker — same as gift flow mobile UI */}
      {!hasFiles && (
        <div className={styles.mobileUploadCenter}>
          <div
            aria-hidden="true"
            className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay1}`}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className={styles.uploadOptionIcon}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 18V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 15L12 12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className={styles.uploadOptionText}>Choose Files from Gallery</span>
          </div>
          <input ref={fileInputRef} type="file" accept="video/*,image/*" multiple onChange={handleFileSelect} className={styles.fileInput} />
        </div>
      )}

      {/* Upload progress — same as gift flow selectedFilesContainer */}
      {hasFiles && (
        <div className={styles.selectedFilesContainer}>
          {/* Quick add row: tabs left, add more right */}
          <div className={styles.quickAddRow}>
            <div className={styles.contentFilterTabs}>
              {[
                { label: 'All', value: 'all' },
                { label: 'Videos', value: 'videos' },
                { label: 'Photos', value: 'photos' },
              ].map((tab) => (
                <button
                  key={tab.value}
                  type="button"
                  className={`${styles.contentFilterTab}${contentFilter === tab.value ? ` ${styles.contentFilterTabActive}` : ''}`}
                  onClick={() => setContentFilter(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className={styles.quickAddRight}>
              <p className={styles.addMoreLabel}>Add More Media</p>
              <div className={styles.quickAddIcons}>
                <div
                  role="button"
                  tabIndex={0}
                  className={styles.quickAddOption}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => { if (e.key === 'Enter') fileInputRef.current?.click(); }}
                >
                  <svg className={styles.quickAddSvg} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 18V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M9 15L12 12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <input ref={fileInputRef} type="file" accept="video/*,image/*" multiple onChange={handleFileSelect} className={styles.fileInput} />

          {/* Upload grid */}
          <div className={`${styles.uploadProgressList}${contentFilter !== 'all' ? ` ${styles.filteredGrid}` : ''}`}>
            {filteredFiles.length === 0 ? (
              <div className={styles.emptyFilter}>
                {contentFilter === 'videos' ? 'No videos' : 'No photos'}
              </div>
            ) : contentFilter !== 'all' ? (
              /* === FILTERED VIEW (Videos/Photos tabs) — filteredCard style === */
              filteredFiles.map((f) => (
                <div key={f.id} className={`${styles.filteredCard}${isUploading(f.status) ? ` ${styles.filteredCardUploading}` : ''}`}>
                  <div className={styles.filteredCardThumb}>
                    {f.thumbnailUrl ? (
                      <img src={f.thumbnailUrl} alt={f.name} />
                    ) : f.type === 'video' && f.previewUrl ? (
                      <video src={f.previewUrl} muted playsInline preload="metadata" />
                    ) : (
                      <div className={styles.filteredCardPlaceholder}>
                        {f.type === 'video' ? (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg>
                        ) : (
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                        )}
                      </div>
                    )}
                    {/* Video play overlay */}
                    {f.type === 'video' && f.status === UPLOAD_STATUS.COMPLETED && (f.thumbnailUrl || f.previewUrl) && (
                      <div className={styles.filteredVideoPlayOverlay}>
                        <span className={styles.filteredVideoPlayIcon}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="#000"><path d="M8 5v14l11-7z" /></svg>
                        </span>
                      </div>
                    )}
                    {/* Eye icon for album preview */}
                    {f.isPhotoAlbum && f.status === UPLOAD_STATUS.COMPLETED && (
                      <button
                        type="button"
                        className={styles.filteredPreviewEyeButton}
                        title="Preview"
                        onClick={() => setAlbumModal({ photos: files.filter((p) => p.type === 'image') })}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
                        </svg>
                      </button>
                    )}
                    {/* Shimmer overlay + progress bar during upload */}
                    {isUploading(f.status) && (
                      <>
                        <div className={styles.filteredCardShimmer} />
                        <div className={styles.filteredCardProgress}>
                          <div className={styles.filteredCardProgressBar} style={{ width: `${f.progress}%` }} />
                        </div>
                      </>
                    )}
                  </div>
                  <div className={styles.filteredCardInfo}>
                    <div className={styles.filteredCardTitleRow}>
                      <span className={styles.filteredCardTitle}>
                        {f.name.length > 18 ? `${f.name.substring(0, 18)}...` : f.name}
                      </span>
                      {!isUploading(f.status) && (
                        <span className={styles.filteredCardTypeIcon}>
                          {f.type === 'video' ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="23 7 16 12 23 17 23 7" />
                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                              <circle cx="8.5" cy="8.5" r="1.5" />
                              <polyline points="21 15 16 10 5 21" />
                            </svg>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              /* === ALL TAB — uploadProgressItem style with progress bar below === */
              filteredFiles.map((f) => (
                <div key={f.id} className={`${styles.uploadProgressItem}${f.status === UPLOAD_STATUS.PENDING ? ` ${styles.previewLoading}` : ''}`}>
                  {/* Thumbnail */}
                  <div className={styles.filteredCardThumb}>
                    {f.thumbnailUrl ? (
                      <img src={f.thumbnailUrl} alt={f.name} />
                    ) : f.type === 'video' && f.previewUrl ? (
                      <video src={f.previewUrl} muted playsInline preload="metadata" />
                    ) : (
                      <div className={styles.filteredCardPlaceholder}>
                        <div className={styles.shimmerAnimation} />
                      </div>
                    )}
                    {/* Eye icon for album preview */}
                    {f.isPhotoAlbum && f.status === UPLOAD_STATUS.COMPLETED && (
                      <button
                        type="button"
                        className={styles.filteredPreviewEyeButton}
                        title="Preview"
                        onClick={() => setAlbumModal({ photos: files.filter((p) => p.type === 'image') })}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
                        </svg>
                      </button>
                    )}
                    {/* Video play overlay for completed videos */}
                    {f.type === 'video' && f.status === UPLOAD_STATUS.COMPLETED && (f.thumbnailUrl || f.previewUrl) && (
                      <div className={styles.filteredVideoPlayOverlay}>
                        <span className={styles.filteredVideoPlayIcon}>
                          <svg width="22" height="22" viewBox="0 0 24 24" fill="#000"><path d="M8 5v14l11-7z" /></svg>
                        </span>
                      </div>
                    )}
                  </div>
                  {/* Card info */}
                  <div className={styles.cardInfo}>
                    <div className={styles.uploadProgressHeader}>
                      <div className={styles.filteredCardTitleRow}>
                        <span className={styles.uploadFileName}>
                          {f.name.length > 25 ? `${f.name.substring(0, 25)}...` : f.name}
                        </span>
                        {f.status === UPLOAD_STATUS.COMPLETED && (
                          <span className={styles.filteredCardTypeIcon}>
                            {f.type === 'video' ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="23 7 16 12 23 17 23 7" />
                                <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <polyline points="21 15 16 10 5 21" />
                              </svg>
                            )}
                          </span>
                        )}
                      </div>
                      {/* Photo count below title for albums */}
                      {f.isPhotoAlbum && f.status === UPLOAD_STATUS.UPLOADING && (
                        <div className={styles.uploadProgressDetails}>
                          <span>{f.uploadedPhotos || 0} / {f.totalPhotos || 0} photos uploaded</span>
                        </div>
                      )}
                      <div className={styles.uploadStatusActions}>
                        <span className={styles.uploadStatus}>
                          {f.status === UPLOAD_STATUS.FAILED && (
                            <button
                              type="button"
                              onClick={() => handleCancelUpload(f.id)}
                              style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'inherit', font: 'inherit', padding: 0 }}
                              title="Remove"
                            >
                              ✕
                            </button>
                          )}
                          {f.status === UPLOAD_STATUS.UPLOADING && f.isPhotoAlbum && `${f.uploadedPhotos || 0}/${f.totalPhotos || 0} photos`}
                          {f.status === UPLOAD_STATUS.UPLOADING && !f.isPhotoAlbum && `${f.progress}%`}
                          {f.status === UPLOAD_STATUS.PAUSED && 'Paused'}
                          {f.status === UPLOAD_STATUS.PENDING && 'Waiting...'}
                          {f.status === UPLOAD_STATUS.CANCELLED && 'Cancelled'}
                          {f.status === UPLOAD_STATUS.COMPLETED && (
                            <span className={styles.uploadCompleteText}>Complete</span>
                          )}
                        </span>
                        {!f.isPhotoAlbum && (f.status === UPLOAD_STATUS.UPLOADING || f.status === UPLOAD_STATUS.PAUSED) && (
                          <div className={styles.uploadControls}>
                            {f.status === UPLOAD_STATUS.UPLOADING && (
                              <button
                                type="button"
                                className={styles.uploadControlButton}
                                onClick={() => handlePauseUpload(f.id)}
                                title="Pause upload"
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                </svg>
                              </button>
                            )}
                            {f.status === UPLOAD_STATUS.PAUSED && (
                              <button
                                type="button"
                                className={styles.uploadControlButton}
                                onClick={() => handleResumeUpload(f.id)}
                                title="Resume upload"
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </button>
                            )}
                            <button
                              type="button"
                              className={styles.uploadControlButtonCancel}
                              onClick={() => handleCancelUpload(f.id)}
                              title="Cancel upload"
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className={styles.uploadProgressBarContainer}>
                      <div
                        className={`${styles.uploadProgressBar}${f.status === UPLOAD_STATUS.COMPLETED ? ` ${styles.uploadProgressComplete}` : ''}${f.status === UPLOAD_STATUS.FAILED ? ` ${styles.uploadProgressFailed}` : ''}`}
                        style={{ width: `${f.progress}%` }}
                      />
                    </div>
                    <div className={styles.uploadProgressDetails}>
                      {f.status === UPLOAD_STATUS.COMPLETED && (
                        <span className={styles.uploadCompleteText}>Complete</span>
                      )}
                      {f.status === UPLOAD_STATUS.FAILED && (
                        <span className={styles.uploadFailedText}>Failed</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Continue button + file count */}
          <div className={styles.uploadActionsRow}>
            <button
              type="button"
              className={styles.continueButton}
              disabled={files.length === 0 || !files.every((f) => f.status === UPLOAD_STATUS.COMPLETED || f.status === UPLOAD_STATUS.FAILED)}
              onClick={() => setShowCompletePopup(true)}
            >
              Continue
            </button>
            <p className={styles.continueSubText}>
              Please stay on the screen until uploads are fully completed
            </p>
            <span className={styles.filesCount}>
              {`${videoCount} video${videoCount !== 1 ? 's' : ''}, ${photoCount} photo${photoCount !== 1 ? 's' : ''}`}
            </span>
          </div>
        </div>
      )}

      {/* Album photos modal — same as gift flow */}
      {albumModal && (
        <div
          className={styles.albumGridOverlay}
          role="button"
          tabIndex={0}
          onClick={() => setAlbumModal(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') setAlbumModal(null); }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            role="dialog"
            aria-modal="true"
            className={styles.albumGridModal}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button type="button" className={styles.albumGridModalClose} onClick={() => setAlbumModal(null)}>
              &times;
            </button>
            <div className={styles.albumGridModalHeader}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#5aa5ef"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
              {`All photos (${albumModal.photos?.length || 0} photos)`}
            </div>
            <div className={styles.albumGridModalBody}>
              {albumModal.photos && albumModal.photos.length > 0 ? (
                <div className={styles.albumGridPhotos}>
                  {albumModal.photos.map((photo) => (
                    <div key={photo.id} className={styles.albumGridPhotoItem}>
                      {photo.thumbnailUrl ? (
                        <img src={photo.thumbnailUrl} alt={photo.name} />
                      ) : (
                        <div className={styles.albumGridPhotoPlaceholder}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                        </div>
                      )}
                      <span className={styles.albumGridPhotoName}>{photo.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>No photos</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload complete popup — same as gift flow */}
      {showCompletePopup && (
        <div className={styles.popupOverlay}>
          <div className={styles.popupCard}>
            <div className={styles.popupCheckmark}>&#10003;</div>
            <h3 className={styles.popupTitle}>Completed</h3>
            <p className={styles.popupMessage}>
              Your files have been uploaded successfully. You can close this page now.
            </p>
            <button
              type="button"
              className={styles.popupButton}
              onClick={() => { window.close(); window.location.href = 'about:blank'; }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileUpload;
